import { useTranslation } from 'react-i18next';
import { useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Box, Typography, CardMedia, Chip, Skeleton } from '@mui/material';
import noPreviewImage from '@/assets/images/no-preview.png';
import BaseButton from '@/components/ui/BaseButton';
import BaseDialog from '@/components/ui/BaseDialog';
import { AssociateModeRelations, ImageOptions } from '@/types/pages/storeManagement/device/associate';
import BaseFontIcon from '../ui/BaseFontIcon';

type ImageDialogProps = {
    open: boolean;
    options: ImageOptions[];
    relations: AssociateModeRelations;
    handleOnClick: (selections: AssociateModeRelations) => void;
    onClose: () => void;
    onExited?: () => void;
};

// —— 多行省略（最多兩行）——
const TITLE_CLAMP_SX = {
    width: '100%',
    minWidth: 0,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    textAlign: 'center',
} as const;

const SUBTEXT_WRAP_SX = {
    width: '100%',
    minWidth: 0,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    textAlign: 'center',
} as const;

const ImageDialog = ({ open, onClose, options, relations, handleOnClick, onExited }: ImageDialogProps) => {
    const { t } = useTranslation('common');
    const [selections, setSelections] = useState<AssociateModeRelations>(relations);
    const [activeIndex, setActiveIndex] = useState(0);
    const swiperRef = useRef<import('swiper').Swiper | null>(null);

    const activeProductId = String(options[activeIndex]?.id ?? '');
    const activeSelectedId = activeProductId ? selections[activeProductId] : undefined;
    const hasSelected = Boolean(activeSelectedId);

    const maxSteps = options.length;
    const isLast = activeIndex === maxSteps - 1;
    const [loaded, setLoaded] = useState<Record<string, boolean>>({});

    const goNext = () => swiperRef.current?.slideNext();
    const goPrev = () => swiperRef.current?.slidePrev();

    const handleSelectChange = (productId: string, value: number) => {
        setSelections((prev) => ({ ...prev, [productId]: String(value) }));
    };

    return (
        <BaseDialog
            open={open}
            dialogTitle=""
            cancelButtonText=""
            confirmButtonText=""
            showTopRightClose
            onClose={onClose}
            TransitionProps={{
                onEnter: () => {
                    setSelections(relations);
                },
                onExited: () => {
                    setSelections({});
                    setActiveIndex(0);
                    if (onExited) {
                        onExited();
                    }
                },
            }}
        >
            <Box
                data-testid="image-dialog-content"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    mt: 1,
                    width: '100%',
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        '& .swiper-pagination': {
                            position: 'static',
                            mt: 1,
                            display: 'flex',
                            justifyContent: 'center',
                        },
                        '& .swiper-pagination-bullets .swiper-pagination-bullet': {
                            mx: 0.5,
                        },
                        '& .swiper': { paddingBottom: 0 },
                    }}
                >
                    <Swiper
                        onSwiper={(swiper) => (swiperRef.current = swiper)}
                        onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
                        style={{ width: '100%' }}
                        allowTouchMove={false}
                    >
                        {options.map((option) => {
                            const productId = String(option.id);

                            return (
                                <SwiperSlide key={productId}>
                                    <Box
                                        sx={{
                                            py: 1,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: 1,
                                            minWidth: 0,
                                            width: '100%',
                                        }}
                                    >
                                        {/* Title + barcode */}
                                        <Typography variant="h4" sx={TITLE_CLAMP_SX}>
                                            {option.name || t('Empty Product')}
                                        </Typography>

                                        <Typography variant="h4" sx={SUBTEXT_WRAP_SX}>
                                            {option.barcode}
                                        </Typography>

                                        <Box
                                            sx={{
                                                width: '100%',
                                                borderRadius: 2,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                py: 1.25,
                                                px: 0.25,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    borderRadius: 2,
                                                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                                    gap: 1.25,
                                                    maxHeight: 340,
                                                    px: 1,
                                                    overflowY: 'auto',
                                                }}
                                            >
                                                {option.slot.map((o) => {
                                                    const selectedId = selections[productId];
                                                    const isSelected = selectedId === String(o.id);
                                                    const imageKey = `${productId}-${o.id}`;
                                                    return (
                                                        <Box
                                                            data-testid={`image-option-${productId}-${o.id}`}
                                                            key={o.id}
                                                            onClick={() => handleSelectChange(productId, o.id)}
                                                            role="button"
                                                            tabIndex={0}
                                                            sx={{
                                                                position: 'relative',
                                                                borderRadius: 2,
                                                                overflow: 'hidden',
                                                                cursor: 'pointer',
                                                                outline: 'none',
                                                                border: '3px solid',
                                                                borderColor: isSelected ? 'primary.main' : 'divider',
                                                                aspectRatio: '4 / 3',
                                                                minHeight: 140,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                            }}
                                                        >
                                                            {isSelected && (
                                                                <>
                                                                    <Chip
                                                                        color="primary"
                                                                        label={
                                                                            <Box display={'flex'} gap={0.75}>
                                                                                <BaseFontIcon
                                                                                    className="fa-solid fa-regular fa-circle-check"
                                                                                    size={16}
                                                                                />
                                                                                {t('Selected')}
                                                                            </Box>
                                                                        }
                                                                        sx={{
                                                                            position: 'absolute',
                                                                            top: 8,
                                                                            right: 8,
                                                                            zIndex: 1,
                                                                            py: 0.75,
                                                                            fontSize: 12,
                                                                            fontWeight: 500,
                                                                            height: 'auto',
                                                                        }}
                                                                    />
                                                                </>
                                                            )}
                                                            {!loaded[imageKey] && (
                                                                <Skeleton
                                                                    variant="rectangular"
                                                                    width="100%"
                                                                    height="100%"
                                                                    sx={{ position: 'absolute', inset: 0 }}
                                                                />
                                                            )}
                                                            <CardMedia
                                                                component="img"
                                                                src={o.previewUrl}
                                                                alt="preview"
                                                                loading="lazy"
                                                                onError={(_event) => {
                                                                    (_event.currentTarget as HTMLImageElement).src =
                                                                        noPreviewImage;
                                                                }}
                                                                onLoad={() =>
                                                                    setLoaded((prev) => ({ ...prev, [imageKey]: true }))
                                                                }
                                                                sx={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'contain',
                                                                    bgcolor: 'background.default',
                                                                    display: 'block',
                                                                }}
                                                            />
                                                            <Chip
                                                                label={o.img_name}
                                                                sx={{
                                                                    position: 'absolute',
                                                                    bottom: 8,
                                                                    right: 8,
                                                                    zIndex: 1,
                                                                    bgcolor: 'rgba(0,0,0,0.55)',
                                                                    color: 'common.white',
                                                                    fontSize: 12,
                                                                    fontWeight: 500,
                                                                    height: 'auto',
                                                                    py: 0.75,
                                                                }}
                                                            />
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        </Box>
                                    </Box>
                                </SwiperSlide>
                            );
                        })}
                    </Swiper>
                </Box>

                <Box
                    sx={{
                        mt: 1,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                        width: '100%',
                    }}
                >
                    {activeIndex > 0 && (
                        <BaseButton
                            data-testid="image-prev-btn"
                            variant="outlined"
                            color="primary"
                            size="small"
                            sx={{
                                minWidth: 130,
                                px: 1.5,
                                mx: 0.5,
                            }}
                            onClick={goPrev}
                        >
                            {t('Prev')}
                        </BaseButton>
                    )}
                    {!isLast ? (
                        <BaseButton
                            data-testid="image-next-btn"
                            variant="contained"
                            color="primary"
                            size="small"
                            sx={{ minWidth: 130, px: 1.75, mx: 0.5 }}
                            disabled={!hasSelected}
                            onClick={goNext}
                        >
                            {t('Next')}
                        </BaseButton>
                    ) : (
                        <BaseButton
                            data-testid="image-ok-btn"
                            variant="contained"
                            color="success"
                            size="small"
                            sx={{ minWidth: 130, px: 1.75, mx: 0.5 }}
                            disabled={!hasSelected}
                            onClick={() => handleOnClick(selections)}
                        >
                            {t('OK')}
                        </BaseButton>
                    )}
                </Box>
            </Box>
        </BaseDialog>
    );
};

export default ImageDialog;
